import React from 'react';
import {Box, Button} from "@mui/material";
import {Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {useDeclarationForEvent} from "../../hooks/useDeclarationForEvent";
import {useOpenContaminationDeclaration} from "../../hooks/useOpenContaminationDeclaration";
import PrintDeclarationButton from "./PrintDeclarationButton";

/**
 * "Declaration of Contamination" block of the event drawer for an ASSET_REMOVE
 * event: current state plus the single action (create / open). Only shown when
 * the backend offers the form for this event (region allow-list), whether or
 * not one has been started yet; creating then works as from the event table.
 * Stays silent while loading (no spinner that then vanishes) and when the
 * lookup fails, e.g. for an event on a node the user cannot read.
 */
const EventDeclarationSection = ({eventId}: {eventId: string}) => {
    const {t} = useTranslation();
    const {declaration, readiness, available, loading, error} = useDeclarationForEvent(eventId);
    const {openForEvent, busy, canCreate} = useOpenContaminationDeclaration();

    if (error || loading) return null;
    // The section follows the backend's availability alone, declaration or not.
    if (!available) return null;

    const statusText = !declaration
        ? t('No declaration of contamination attached to this removal.')
        : readiness?.ready
            ? t('Complete and ready to print.')
            : readiness && readiness.missingCount > 0
                ? t('Open answers: {{count}}', {count: readiness.missingCount})
                : t('The legally binding declaration has not been accepted yet.');

    return (
        <Box sx={{display: 'flex', flexDirection: 'column'}} data-testid="event-doc-section">
            <Typography level="bodyBold">
                {t('Declaration of Contamination')}
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '13px', pl: '8px', pt: '13px'}}>
                <Typography level="body">{statusText}</Typography>
                {declaration?.updatedAt && (
                    <Typography level="bodySmall" sx={{color: 'text.secondary'}}>
                        {t('Last edited')}: {moment(declaration.updatedAt).format('L LT')}
                    </Typography>
                )}
                {(declaration || canCreate) && (
                    <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                        <Button
                            variant={declaration ? 'outlined' : 'contained'}
                            size="small"
                            onClick={() => openForEvent(eventId)}
                            disabled={busy}
                            data-testid="event-doc-open"
                        >
                            {declaration ? t('Open declaration') : t('Create declaration')}
                        </Button>
                        {declaration && readiness?.ready && (
                            <PrintDeclarationButton
                                declarationId={declaration.id}
                                data-testid="event-doc-print"
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default EventDeclarationSection;
